import { Form, Input, Button, Card, Select, message } from "antd";
import { FormInstance } from "antd/es/form";
import { useEffect, useState } from "react";
import { testAiProfile } from "@/lib/api/resources/agents";
import { useNavigate, useLocation } from "react-router-dom";

// Providers that talk to a self-hosted endpoint and therefore need an explicit host in the
// connection. Cloud providers always use their standard API endpoint (a null host), so the
// endpoint field is hidden for them. Mirrors the backend PlatformResolver::requiresExplicitHost().
const SELF_HOSTED_PROVIDERS = ["ollama"];

// Per-provider example model, shown as the Model field placeholder.
const MODEL_PLACEHOLDER: Record<string, string> = {
  openai: "e.g. gpt-4o",
  anthropic: "e.g. claude-opus-4-8",
  mistral: "e.g. mistral-small-latest",
  gemini: "e.g. gemini-2.5-flash",
  openrouter: "e.g. openai/gpt-4o",
  ollama: "e.g. llama3.2",
};

interface AiProfileFormProps {
  form: FormInstance;
  onFinish: (values: any) => void;
  initialValues?: any;
  isSubmitting?: boolean;
  profileId?: string;
}

export const AiProfileForm: React.FC<AiProfileFormProps> = ({
  form,
  onFinish,
  initialValues,
  isSubmitting,
  profileId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isTesting, setIsTesting] = useState(false);
  const selectedProvider: string | undefined = Form.useWatch("provider", form);

  const fromSearch = location.state?.fromSearch || "";
  const isSelfHosted =
    !!selectedProvider && SELF_HOSTED_PROVIDERS.includes(selectedProvider);

  useEffect(() => {
    if (initialValues) {
      // The raw connection secret (API key) is never returned by the API, so drive the non-secret
      // fields from the read-safe `aiConnection` (sourced from the model) instead; the API key
      // must be re-entered to save (security).
      const params = initialValues.aiConnection || {};

      // The "default" sentinel host means the provider's standard endpoint; leave the host field
      // empty in that case so it's not mistaken for a custom/self-hosted endpoint.
      const host =
        params.host && params.host !== "default"
          ? params.port
            ? `${params.host}:${params.port}`
            : params.host
          : undefined;

      form.setFieldsValue({
        name: initialValues.name,
        provider: params.provider,
        model: params.model,
        // apiKey is never returned on read; it must be re-entered by the user to keep or change
        // the connection.
        apiKey: undefined,
        host,
        temperature: params.options?.temperature,
      });
    }
  }, [initialValues, form]);

  const getPayload = (values: any) => {
    const trimmedHost = (values.host || "").trim();
    let host: string | null = null;
    let port: number | null = null;
    if (trimmedHost) {
      const separatorIndex = trimmedHost.lastIndexOf(":");
      if (separatorIndex > -1) {
        const hostPart = trimmedHost.slice(0, separatorIndex);
        const portPart = trimmedHost.slice(separatorIndex + 1);
        host = hostPart || null;
        port = portPart ? Number(portPart) : null;
      } else {
        host = trimmedHost;
        port = null;
      }
    }

    return {
      name: values.name || "Test Profile",
      provider: values.provider,
      model: values.model,
      apiKey: values.apiKey || null,
      host,
      port,
      temperature:
        values.temperature != null && values.temperature !== ""
          ? Number(values.temperature)
          : null,
    };
  };

  const handleSubmit = (values: any) => {
    onFinish(getPayload(values));
  };

  const handleTestConnection = async () => {
    try {
      const values = await form.validateFields();
      setIsTesting(true);
      const payload = getPayload(values);
      const result = await testAiProfile(payload);

      if (result.success) {
        message.success({
          content: result.message,
          duration: 5,
        });
      } else {
        message.error({
          content: result.message,
          duration: 10,
        });
      }
    } catch (e: any) {
      if (e.errorFields) {
        message.error("Please fill in all required fields first.");
      } else {
        message.error("An error occurred while testing the connection.");
      }
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Card className="shadow-sm border-slate-200/60 rounded-xl overflow-hidden">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="max-w-3xl"
      >
        <div className="bg-slate-50 border-b border-slate-100 -mx-6 -mt-6 p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">
            General Information
          </h2>
          <p className="text-sm text-slate-500 mb-4">
            Name your profile so it can be easily identified in the
            Configuration menus.
          </p>
          <Form.Item
            name="name"
            label="Profile Name"
            rules={[{ required: true, message: "A profile name is required." }]}
          >
            <Input placeholder="e.g. Production GPT-4 Turbo" size="large" />
          </Form.Item>
        </div>

        <div className="px-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Connection Parameters
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="provider"
              label="AI Provider"
              rules={[{ required: true, message: "Provider is required" }]}
            >
              <Select size="large">
                <Select.Option value="openai">OpenAI</Select.Option>
                <Select.Option value="anthropic">Anthropic</Select.Option>
                <Select.Option value="mistral">Mistral</Select.Option>
                <Select.Option value="gemini">Google Gemini</Select.Option>
                <Select.Option value="openrouter">OpenRouter</Select.Option>
                <Select.Option value="ollama">Ollama</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="model"
              label="Model"
              rules={[{ required: true, message: "Model is required" }]}
            >
              <Input
                placeholder={
                  MODEL_PLACEHOLDER[selectedProvider ?? ""] ?? "e.g. gpt-4o"
                }
                size="large"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="apiKey"
            label="API Key"
            extra={
              <span className="text-slate-400 text-xs">
                {profileId
                  ? "For security, the saved API key is never displayed. Re-enter it to keep or change the connection; leave empty to inherit the secure platform default key."
                  : "Leave empty to inherit the secure platform default API key."}
              </span>
            }
          >
            <Input.Password
              placeholder="Inherit Platform Default Key"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="host"
            label={
              isSelfHosted ? "API Endpoint" : "Custom API Endpoint (Optional)"
            }
            rules={
              isSelfHosted
                ? [
                    {
                      required: true,
                      message:
                        "An endpoint host is required for self-hosted providers.",
                    },
                  ]
                : []
            }
            extra={
              <span className="text-slate-400 text-xs">
                {isSelfHosted
                  ? "The self-hosted server host (assumed HTTPS), e.g. my-ollama.internal:11434."
                  : "Leave empty for the provider's standard endpoint. Set an OpenAI-compatible host (proxy/gateway, e.g. my-gateway.internal) to route through it instead."}
              </span>
            }
          >
            <Input
              placeholder={
                isSelfHosted
                  ? "e.g. my-ollama.internal:11434"
                  : "Optional — e.g. my-gateway.internal"
              }
              size="large"
            />
          </Form.Item>

          <Form.Item name="temperature" label="Default Temperature (Optional)">
            <Input
              type="number"
              step="0.1"
              min="0"
              max="2"
              placeholder="0.7"
              size="large"
            />
          </Form.Item>

          <div className="pt-6 border-t border-slate-100 flex justify-between mt-8">
            <Button
              size="large"
              onClick={handleTestConnection}
              loading={isTesting}
              disabled={isSubmitting}
            >
              Test Connection
            </Button>
            <div className="flex gap-3">
              <Button
                size="large"
                onClick={() => navigate(`/ai-profiles${fromSearch}`)}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={isSubmitting}
                disabled={isTesting}
                className="bg-indigo-600 hover:bg-indigo-700 border-none px-8"
              >
                Save Profile
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </Card>
  );
};
